import React, { useState } from "react";
import { Form } from "react-crud-form";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "ui/components/dialog";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import { Button } from "ui/components/button";
import { FormLabel } from "components/form";
import EvtMultiSelectBadges from "components/multiselect";
import TiptapEditor from "components/tiptap-editor";
import EmployeeInput from "components/employee/input";
import { FormActions } from "components/form/actions";
import HeaderPortal from "components/portal/header-portal";
import { StatusTabInput } from "components/status/tabs/input";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import ButtonWithLoading from "@/components/button-with-loading";
import { useOpeningForm } from "./useOpeningForm";
import { openingSchema } from "./schema";
import ExpertiseInput from "../../components/expertise-input";

const OpeningForm = ({ openingId }) => {
    const navigate = useNavigate();
    const { form, handleCreate, handleUpdate, handleRead, handleArchive, requiredFields } = useOpeningForm({
        openingId,
    });

    const isPublished = form.watch("status") === "published";
    const status = form.watch("status");

    const [confirmation, setConfirmation] = useState({
        open: false,
        title: "",
        description: "",
        action: null,
    });
    const [actionLoading, setActionLoading] = useState(false);

    const handleConfirm = async () => {
        if (!confirmation.action) return;
        setActionLoading(true);
        try {
            await confirmation.action();
            setConfirmation({ ...confirmation, open: false });
        } catch (error) {
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Form
            form={form}
            create={handleCreate}
            read={handleRead}
            update={handleUpdate}
            itemId={openingId || "+"}
            schema={openingSchema}
        >
            {({
                isCreating,
                createItem,
                isNewItem,
                isUpdating,
                updateItem,
                isDirty,
            }) => (
                <div className="space-y-6">
                    <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Status
                                    </FormLabel>
                                    <StatusTabInput
                                        key={"form-input"}
                                        {...field}
                                        collection="openings"
                                        remove={["archived"]}
                                        disabled={isPublished}
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Title */}
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>Title</FormLabel>
                                    <Input
                                        type="text"
                                        value={field.value ?? ""}
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.value === ""
                                                    ? undefined
                                                    : e.target.value
                                            )
                                        }
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Opportunity Link */}
                    <FormField
                        control={form.control}
                        name="opportunity_link"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>Opportunity Link</FormLabel>
                                    <Input
                                        type="url"
                                        value={field.value ?? ""}
                                        onChange={field.onChange}
                                        disabled={isPublished}
                                        placeholder="https://example.com/opportunity"
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Skills */}
                    <FormField
                        control={form.control}
                        name="skills"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    type="form"
                                    field={field.name}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Skills
                                    </FormLabel>
                                    <EvtMultiSelectBadges
                                        collection="skills"
                                        values={field.value || []}
                                        onChange={(val) => field.onChange(val)}
                                        placeholder="Select skills"
                                        disabled={isPublished}
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Expertise */}
                    <FormField
                        control={form.control}
                        name="expertise"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>Expertise</FormLabel>
                                    <ExpertiseInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={isPublished}
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Sales Person */}
                    <FormField
                        control={form.control}
                        name="sales_coordinator"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    type="form"
                                    field={field.name}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Sales Person
                                    </FormLabel>
                                    <EmployeeInput set="sales_coordinating" {...field} disabled={isPublished} />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Description */}
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="openings"
                                    type="form"
                                    field={field.name}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Description
                                    </FormLabel>
                                    <TiptapEditor {...field} className="min-h-32" />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormActions
                        isNewItem={isNewItem}
                        isCreating={isCreating}
                        isUpdating={isUpdating}
                        isDirty={isDirty}
                        createItem={() => createItem()}
                        updateItem={() => updateItem().then(() => navigate(-1))}
                        showCancel={false}
                        collection="openings"
                        rule={isNewItem ? "create:*" : "update:*"}
                    />

                    {!isNewItem && (
                        <HeaderPortal>
                            <PermissionGate
                                rule="update:*"
                                collection="openings"
                            >
                                <PermissionControlledInput
                                    rule="update:*"
                                    collection="openings"
                                >
                                    <ButtonWithLoading
                                        onClick={() =>
                                            setConfirmation({
                                                open: true,
                                                title: "Archive this Opening",
                                                description:
                                                    "This action will Archive this opening. No new applications can be submitted. Existing data and history will remain available. This action does not delete any data.",
                                                action: () => handleArchive(openingId),
                                            })
                                        }
                                        disabled={status === "archived" || isUpdating}
                                        variant="outline"
                                    >
                                        {status === "archived" ? "Archived" : "Archive"}
                                    </ButtonWithLoading>
                                </PermissionControlledInput>
                            </PermissionGate>
                        </HeaderPortal>
                    )}

                    <Dialog
                        open={confirmation.open}
                        onOpenChange={(open) =>
                            setConfirmation((prev) => ({ ...prev, open }))
                        }
                    >
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{confirmation.title}</DialogTitle>
                                <DialogDescription className="text-red-500">
                                    {confirmation.description}
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter className="gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        setConfirmation((prev) => ({ ...prev, open: false }))
                                    }
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoading
                                    onClick={handleConfirm}
                                    isLoading={actionLoading}
                                    variant="default"
                                >
                                    Confirm
                                </ButtonWithLoading>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            )}
        </Form>
    );
};

export default OpeningForm;
